/*
 * InfoHint — a small (i) button that opens a centered popup explainer.
 *
 * Earlier version rendered the disclosure as an inline sibling of the
 * button. That looked fine next to a wide label, but inside a tight
 * grid cell (e.g. the Round Setup card columns) the card was squeezed
 * into a narrow tall column and read terribly. The popup overlay
 * sidesteps the parent's layout entirely so the card always has the
 * same comfortable width regardless of where the (i) button lives.
 *
 * Visual:
 *   - Small circular (i) icon inline with the surrounding label.
 *   - Click → fixed-position backdrop + centered popup card.
 *   - Click outside / Escape / Close button dismiss.
 *   - Touch-friendly: backdrop has padding so the popup stays clear of
 *     edges; close button is a 36×36 hit target.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Info, X } from 'lucide-react';

export interface InfoHintProps {
  /** Short title shown at the top of the popup. Usually matches the
   *  setting label, e.g. "Net Course Handicap". */
  title: string;
  /** One-sentence definition. */
  body: ReactNode;
  /** A worked example. Rendered with monospace numbers for the math. */
  example?: ReactNode;
  /** Accessible label for the button itself. Defaults to "More info". */
  ariaLabel?: string;
}

export function InfoHint({ title, body, example, ariaLabel = 'More info' }: InfoHintProps) {
  const [open, setOpen] = useState(false);

  // Escape closes the popup. Listener only when open so we're not
  // intercepting Escape elsewhere on the page.
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // Stop propagation so clicking the (i) inside a Link or other
          // clickable parent doesn't also activate that parent.
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label={ariaLabel}
        aria-expanded={open}
        title={ariaLabel}
        style={{
          // 40x40 hit area with the 18x18 visual circle centered inside —
          // clears the Apple HIG touch floor so the (i) is reliably
          // tappable on phones without crowding the label visually.
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 40,
          minHeight: 40,
          width: 40,
          height: 40,
          padding: 0,
          marginLeft: 2,
          borderRadius: 16,
          border: 'none',
          background: 'transparent',
          color: open ? 'var(--green-bright)' : 'var(--text-mut)',
          cursor: 'pointer',
          verticalAlign: 'middle',
          flexShrink: 0,
        }}
      >
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: 9,
          border: `1px solid ${open ? 'var(--green-bright)' : 'var(--border)'}`,
          background: open ? 'rgba(34,197,94,0.10)' : 'transparent',
        }}>
          <Info width={11} height={11} strokeWidth={2.5} />
        </span>
      </button>

      {open && (
        <div
          role="presentation"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 1100,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card)',
              border: '1px solid var(--green-bright)',
              borderRadius: 14,
              padding: '18px 18px 16px',
              maxWidth: 420,
              width: '100%',
              maxHeight: 'calc(100dvh - 40px)',
              overflowY: 'auto',
              boxShadow: '0 24px 50px rgba(0,0,0,0.45)',
              color: 'var(--text-sec)',
              fontSize: 14,
              lineHeight: 1.55,
              // Hard-reset inherited text styling. The (i) button often
              // sits inside uppercase/letterspaced label rows (SetupField,
              // t-label) and the popup was inheriting ALL-CAPS + tracking,
              // which made the body read like it was shouting.
              textTransform: 'none',
              letterSpacing: 'normal',
              textAlign: 'left',
              fontWeight: 400,
            }}
          >
            {/* Header: green title + close button */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: 'var(--green-bright)',
                }}
              >
                {title}
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-mut)',
                  cursor: 'pointer',
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: -6,
                  marginRight: -6,
                }}
              >
                <X width={18} height={18} />
              </button>
            </div>

            <div style={{ color: 'var(--text)' }}>{body}</div>

            {example && (
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px dashed rgba(34,197,94,0.4)',
                  fontSize: 13,
                  color: 'var(--text-sec)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <strong style={{ color: 'var(--text)' }}>Example: </strong>{example}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
